// MediaViewer: the one image viewer for App grids, tables and boards, Artifact image blocks and
// Chat thumbnail strips (DES1-specs "images", DES2-image-ux.md §A). Desktop: centred over a
// scrim. Phone: full screen with pinch, double-tap, swipe and pull-down. Only same-origin sizes
// ever load (model.ts safeSrc); a media ref that isn't ready shows its labelled box, never a
// broken image.
//
// Exportable as is: no workspace, artifact or app-runtime imports, so the App Kit can re-export it.
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ComponentType,
  type ImgHTMLAttributes,
} from 'react';
import { useAppCatalogLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { mediaCopy } from '@/i18n/ns/media';
import { Crossfade, DUR, EASE, useReducedMotion } from '@/lib/motion';
import { MEDIA_PLACEHOLDER_GLYPH as GLYPH } from './glyph';
import {
  STATE_COPY,
  ZOOM_DOUBLE,
  clampPan,
  cssRatio,
  dismissRelease,
  dragOffset,
  navRelease,
  pickSize,
  shownState,
  thumbSize,
  zoomAround,
  type MediaItem,
  type MediaView,
} from './model';

export type { MediaItem, MediaSize, MediaState } from './model';

type Props = {
  items: readonly MediaItem[];
  index: number;
  onIndexChange: (index: number) => void;
  /** Called once the close motion has finished; the parent unmounts the viewer. */
  onClose: () => void;
  /** The element that opened item `i` (its thumbnail), for the move and for focus on close. */
  origin?: (i: number) => HTMLElement | null;
  phone?: boolean;
  /** Dates in the viewer's locale (the App Kit passes its viewer locale); else the interface's. */
  locale?: string;
  /** A caller's authenticated loader; App Kit images keep their ordinary element. */
  imageComponent?: ComponentType<ImgHTMLAttributes<HTMLImageElement>> | 'img';
};

type Pan = { x: number; y: number };
type Gesture = {
  pointers: Map<number, { x: number; y: number }>;
  start: { x: number; y: number; t: number };
  axis: 'x' | 'y' | 'pan' | 'pinch' | null;
  pinch?: { dist: number; scale: number; pan: Pan };
  panStart?: Pan;
  moved: boolean;
};

const NO_MOVE = 8;
const stamp = (event: { timeStamp: number }) => event.timeStamp || performance.now();
const DOUBLE_MS = 300;

/**
 * Stable geometry for zoom and pan: the stage's centre (the stage is never scaled) and the
 * figure's layout size (offsetWidth/Height ignore transforms), so the current scale is never
 * counted twice. Without layout sizes (non-browser), the rect divided by the scale.
 */
function layoutOf(stage: HTMLElement, figure: HTMLElement, scale: number) {
  const s = stage.getBoundingClientRect();
  const f = figure.getBoundingClientRect();
  return {
    cx: s.left + s.width / 2,
    cy: s.top + s.height / 2,
    width: figure.offsetWidth || f.width / scale,
    height: figure.offsetHeight || f.height / scale,
  };
}
/** Load state per item and source, so an event for an earlier item or source never counts. */
const sourceKey = (id: string, src: string) => `${id}\u0000${src}`;
const distance = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  Math.hypot(a.x - b.x, a.y - b.y);
const animate = (el: Element | null, frames: Keyframe[], ms: number, easing: string) =>
  el && typeof (el as HTMLElement).animate === 'function'
    ? (el as HTMLElement).animate(frames, { duration: ms, easing })
    : null;

/** The inverse transform from the viewer's box to an origin box (for the shared-element move). */
function fromBox(target: Element, origin: Element): string | null {
  const a = origin.getBoundingClientRect();
  const b = target.getBoundingClientRect();
  if (!a.width || !b.width) return null;
  const s = a.width / b.width;
  const dx = a.left + a.width / 2 - (b.left + b.width / 2);
  const dy = a.top + a.height / 2 - (b.top + b.height / 2);
  return `translate(${dx}px, ${dy}px) scale(${s})`;
}
const onScreen = (el: Element) => {
  const r = el.getBoundingClientRect();
  const view = el.ownerDocument.defaultView;
  return (
    !!view &&
    r.width > 0 &&
    r.bottom > 0 &&
    r.right > 0 &&
    r.top < view.innerHeight &&
    r.left < view.innerWidth
  );
};

export function MediaViewer({
  items,
  index,
  onIndexChange,
  onClose,
  origin,
  phone = false,
  locale: viewerLocale,
  imageComponent: ImageElement = 'img',
}: Props) {
  const copy = useNamespaceCopy(mediaCopy);
  const interfaceLocale = useAppCatalogLocale();
  const locale = viewerLocale ?? interfaceLocale;
  const reduce = useReducedMotion();
  const root = useRef<HTMLDivElement>(null);
  const figure = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const gesture = useRef<Gesture | null>(null);
  const lastTap = useRef<{ t: number; x: number; y: number } | null>(null);
  const closing = useRef(false);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState<Pan>({ x: 0, y: 0 });
  const [drag, setDrag] = useState<Pan>({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dir, setDir] = useState<-1 | 0 | 1>(0);
  const [sources, setSources] = useState<Record<string, 'loaded' | 'failed'>>({});
  const settle = (key: string, value: 'loaded' | 'failed') =>
    setSources((now) => (now[key] === value ? now : { ...now, [key]: value }));
  const titleId = useId();
  // The opener's theme: a dark ancestor of the thumbnail, or a dark document.
  const [dark] = useState(() => {
    const from = origin?.(index);
    const doc = from?.ownerDocument ?? (typeof document === 'undefined' ? null : document);
    return !!from?.closest('.dark') || !!doc?.documentElement.classList.contains('dark');
  });
  const item = items[index];
  const count = items.length;
  const state = item ? shownState(item) : 'missing';

  const when = (iso: string) => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
      ? ''
      : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
  };
  const need = () => {
    const view = root.current?.ownerDocument.defaultView;
    const ratio = view?.devicePixelRatio ?? 1;
    return Math.round((view?.innerWidth ?? 1200) * (phone ? 1 : 0.9) * ratio);
  };

  // Open: from the thumbnail's box into place; focus starts on Close.
  useEffect(() => {
    // Focus starts on the dialog itself, so a pointer open shows no ring; Tab reaches Close.
    root.current?.focus();
    const from = origin?.(index);
    if (reduce || !from || !figure.current) return;
    const start = fromBox(figure.current, from);
    if (start)
      animate(
        figure.current,
        [{ transform: start }, { transform: 'none' }],
        DUR.standard,
        EASE.enter,
      );
    // Only when the viewer opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Neighbours at screen size, so the next swipe lands on a decoded image.
  useEffect(() => {
    if (ImageElement !== 'img' || typeof Image !== 'function') return;
    for (const i of [index - 1, index + 1]) {
      const size = items[i] && shownState(items[i]) === 'ready' ? pickSize(items[i], need()) : null;
      if (size) new Image().src = size.src;
    }
    // need() reads the window; the index is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, items, ImageElement]);

  const close = useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    const target = origin?.(index) ?? null;
    const done = () => {
      onClose();
      target?.focus();
    };
    const el = figure.current;
    if (reduce || !el) return done();
    const back = target && onScreen(target) ? fromBox(el, target) : null;
    const run = back
      ? animate(el, [{ transform: 'none' }, { transform: back }], DUR.standard, EASE.enter)
      : animate(
          el,
          [
            { opacity: 1, transform: 'none' },
            { opacity: 0, transform: 'scale(0.98)' },
          ],
          DUR.quick,
          EASE.cross,
        );
    if (!run) return done();
    run.onfinish = done;
  }, [index, onClose, origin, reduce]);

  const go = useCallback(
    (step: -1 | 1) => {
      const next = index + step;
      if (next < 0 || next >= count) return;
      setScale(1);
      setPan({ x: 0, y: 0 });
      setDir(reduce ? 0 : step);
      onIndexChange(next);
    },
    [count, index, onIndexChange, reduce],
  );

  const zoomTo = (next: number, x: number, y: number) => {
    if (!figure.current || !stage.current || !Number.isFinite(next) || next <= 0) return;
    const box = layoutOf(stage.current, figure.current, scale);
    const z = zoomAround(scale, pan, next, { x: x - box.cx, y: y - box.cy });
    setScale(z.scale);
    setPan(clampPan(z.pan, z.scale, box));
  };

  // A different image starts fresh: no gesture, zoom or pan carries over. Identity is the item,
  // its state and its sources, so a host re-projecting items[index] in place resets too.
  const imageKey = item
    ? [item.id, item.state, ...(item.sizes ?? []).map((size) => size.src)].join('\u0000')
    : '';
  useEffect(() => {
    gesture.current = null;
    lastTap.current = null;
    setDragging(false);
    setDrag({ x: 0, y: 0 });
    setScale(1);
    setPan({ x: 0, y: 0 });
  }, [imageKey]);

  // Focus never leaves the open viewer: if it lands outside (a control that just became
  // disabled, a stray click), it comes back to the dialog.
  const onBlurCapture = () => {
    setTimeout(() => {
      const el = root.current;
      if (!el || closing.current || !el.isConnected) return;
      const active = el.ownerDocument.activeElement;
      if (!active || !el.contains(active)) el.focus();
    }, 0);
  };

  // Ctrl/⌘ + wheel zooms; a plain wheel never scrolls the page behind the viewer.
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const wheel = (event: WheelEvent) => {
      event.preventDefault();
      if (event.ctrlKey || event.metaKey)
        zoomTo(scale * (event.deltaY < 0 ? 1.15 : 1 / 1.15), event.clientX, event.clientY);
    };
    el.addEventListener('wheel', wheel, { passive: false });
    return () => el.removeEventListener('wheel', wheel);
  });

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      return close();
    }
    if (event.key === 'ArrowLeft') return go(-1);
    if (event.key === 'ArrowRight') return go(1);
    if (event.key !== 'Tab' || !root.current) return;
    // Focus stays inside the viewer.
    const focusable = [...root.current.querySelectorAll<HTMLElement>('button:not([disabled])')];
    const first = focusable[0];
    const last = focusable.at(-1);
    const active = root.current.ownerDocument.activeElement;
    // From the dialog itself (where focus starts) or anywhere that isn't a control: Tab goes
    // to the first control, Shift+Tab to the last, never out to the page.
    if (!active || !focusable.includes(active as HTMLElement)) {
      event.preventDefault();
      (event.shiftKey ? last : first)?.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const g: Gesture = gesture.current ?? {
      pointers: new Map(),
      start: { x: event.clientX, y: event.clientY, t: stamp(event) },
      axis: null,
      moved: false,
    };
    g.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (g.pointers.size === 2) {
      const [a, b] = [...g.pointers.values()];
      g.axis = 'pinch';
      g.moved = true;
      // Two fingers on one spot have no distance yet; the first move that has one sets it.
      g.pinch = { dist: distance(a, b), scale, pan };
    }
    gesture.current = g;
    setDragging(true);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    if (!g?.pointers.has(event.pointerId)) return;
    g.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (g.axis === 'pinch' && g.pinch && g.pointers.size === 2) {
      const [a, b] = [...g.pointers.values()];
      const d = distance(a, b);
      if (g.pinch.dist < 1) {
        g.pinch = { dist: d, scale, pan };
        return;
      }
      return zoomTo((g.pinch.scale * d) / g.pinch.dist, (a.x + b.x) / 2, (a.y + b.y) / 2);
    }
    if (g.axis === 'pinch') return;
    const dx = event.clientX - g.start.x;
    const dy = event.clientY - g.start.y;
    if (!g.axis && Math.hypot(dx, dy) > NO_MOVE) {
      g.axis = scale > 1 ? 'pan' : Math.abs(dx) >= Math.abs(dy) || !phone ? 'x' : 'y';
      g.panStart = pan;
    }
    if (!g.axis) return;
    g.moved = true;
    if (g.axis === 'pan' && g.panStart && figure.current && stage.current)
      setPan(
        clampPan(
          { x: g.panStart.x + dx, y: g.panStart.y + dy },
          scale,
          layoutOf(stage.current, figure.current, scale),
        ),
      );
    else if (g.axis === 'x') setDrag({ x: dragOffset(dx, index, count), y: 0 });
    else if (g.axis === 'y') setDrag({ x: 0, y: Math.max(0, dy) });
  };

  const tapped = (event: PointerEvent<HTMLDivElement>) => {
    const now = stamp(event);
    const prev = lastTap.current;
    if (
      prev &&
      now - prev.t < DOUBLE_MS &&
      distance(prev, { x: event.clientX, y: event.clientY }) < 24
    ) {
      lastTap.current = null;
      return zoomTo(scale > 1 ? 1 : ZOOM_DOUBLE, event.clientX, event.clientY);
    }
    lastTap.current = { t: now, x: event.clientX, y: event.clientY };
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    if (!g?.pointers.has(event.pointerId)) return;
    g.pointers.delete(event.pointerId);
    if (g.pointers.size > 0) {
      // One finger left after a pinch: it pans (or waits) from here; the release is no tap.
      const [rest] = [...g.pointers.values()];
      g.start = { x: rest.x, y: rest.y, t: stamp(event) };
      g.axis = scale > 1 ? 'pan' : null;
      g.panStart = pan;
      g.pinch = undefined;
      return;
    }
    gesture.current = null;
    setDragging(false);
    const dt = Math.max(1, stamp(event) - g.start.t);
    const dx = event.clientX - g.start.x;
    const dy = event.clientY - g.start.y;
    setDrag({ x: 0, y: 0 });
    if (!g.moved) return tapped(event);
    if (g.axis === 'x') {
      const width = stage.current?.getBoundingClientRect().width || 1;
      const step = navRelease(dx, dx / dt, width, index, count);
      if (step) go(step);
    } else if (g.axis === 'y' && dismissRelease(dy, dy / dt)) close();
  };

  const onPointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    g?.pointers.delete(event.pointerId);
    if (g?.pointers.size) {
      const [rest] = [...g.pointers.values()];
      g.start = { x: rest.x, y: rest.y, t: stamp(event) };
      g.axis = scale > 1 ? 'pan' : null;
      g.panStart = pan;
      g.pinch = undefined;
      return;
    }
    gesture.current = null;
    setDragging(false);
    setDrag({ x: 0, y: 0 });
  };

  if (!item) return null;
  const thumb = thumbSize(item);
  const wanted = pickSize(item, need());
  const large = wanted && thumb && wanted.src !== thumb.src ? wanted : null;
  const thumbKey = thumb ? sourceKey(item.id, thumb.src) : '';
  const largeKey = large ? sourceKey(item.id, large.src) : '';
  const thumbFailed = sources[thumbKey] === 'failed';
  const largeFailed = !large || sources[largeKey] === 'failed';
  const largeLoaded = !!large && sources[largeKey] === 'loaded';
  // Bytes that fail to load or decode read as refused, never a broken image. A failed large size
  // keeps the thumbnail with the note under it; a failed thumbnail waits for the large size.
  const view: MediaView =
    state !== 'ready' || !thumb
      ? state
      : thumbFailed && largeFailed
        ? 'refused'
        : thumbFailed && !largeLoaded
          ? 'loading'
          : 'ready';
  const fade = drag.y > 0 ? Math.max(0, 1 - drag.y / (3 * 120)) : 1;
  const placeholder =
    view === 'ready'
      ? null
      : (view === 'expired' || view === 'withdrawn') && item.seenAt
        ? formatCopy(copy.expiredSeen, { date: when(item.seenAt) })
        : copy[STATE_COPY[view]];

  return (
    <div
      ref={root}
      // pc-portal: the token scope, so the viewer is themed on any origin (the App runtime too);
      // the token dark scope is same-element, so the root also mirrors the opener's theme.
      className={dark ? 'pc-media-viewer pc-portal dark' : 'pc-media-viewer pc-portal'}
      data-testid="media-viewer"
      data-mode={phone ? 'phone' : 'desktop'}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      onBlurCapture={onBlurCapture}
    >
      <div
        className="pc-media-scrim"
        data-testid="media-scrim"
        style={{ opacity: fade }}
        onClick={phone ? undefined : close}
      />
      <div className="pc-media-bar">
        <span className="pc-media-counter" aria-hidden="true">
          {formatCopy(copy.counter, { n: index + 1, total: count })}
        </span>
        <span id={titleId} className="pc-media-sr">
          {item.alt || copy.viewer}
        </span>
        <button
          type="button"
          className="pc-btn"
          // Over the desktop scrim a quiet button is too faint; the phone surface is solid.
          data-variant={phone ? 'quiet' : 'secondary'}
          onClick={close}
        >
          {copy.close}
        </button>
      </div>
      <div
        className="pc-media-stage"
        ref={stage}
        data-testid="media-stage"
        data-dragging={dragging || undefined}
        style={{ transform: `translate(${drag.x}px, ${drag.y}px)` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onLostPointerCapture={onPointerCancel}
      >
        <div
          key={item.id}
          ref={figure}
          className="pc-media-figure"
          data-dir={dir || undefined}
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})` }}
          data-scale={scale}
        >
          {state === 'ready' && thumb && !(thumbFailed && largeFailed) && (
            <Crossfade
              show={largeLoaded || thumbFailed ? 'b' : 'a'}
              a={
                thumbFailed ? null : (
                  <ImageElement
                    src={thumb.src}
                    alt={largeLoaded ? '' : item.alt}
                    draggable={false}
                    onLoad={() => settle(thumbKey, 'loaded')}
                    onError={() => settle(thumbKey, 'failed')}
                  />
                )
              }
              b={
                large && !largeFailed ? (
                  <ImageElement
                    src={large.src}
                    alt={largeLoaded ? item.alt : ''}
                    draggable={false}
                    onLoad={() => settle(largeKey, 'loaded')}
                    onError={() => settle(largeKey, 'failed')}
                  />
                ) : null
              }
            />
          )}
          {/* Over the pixels while a ready item's large size is still arriving (its loader stays
              mounted underneath so it can settle), or alone for every other state. */}
          {view !== 'ready' && (
            <div
              className="pc-media-placeholder"
              data-state={view}
              role="img"
              aria-label={placeholder ?? ''}
              style={{ aspectRatio: cssRatio(item.ratio) ?? undefined }}
            >
              <svg className="pc-media-glyph" viewBox={GLYPH.viewBox} aria-hidden="true">
                <rect {...GLYPH.rect} />
                <circle {...GLYPH.circle} />
                <path d={GLYPH.path} />
              </svg>
              <span aria-hidden="true">{placeholder}</span>
            </div>
          )}
        </div>
      </div>
      {!phone && (
        <>
          <button
            type="button"
            className="pc-media-edge"
            data-side="previous"
            aria-label={copy.previous}
            disabled={index === 0}
            onClick={() => go(-1)}
          >
            ‹
          </button>
          <button
            type="button"
            className="pc-media-edge"
            data-side="next"
            aria-label={copy.next}
            disabled={index === count - 1}
            onClick={() => go(1)}
          >
            ›
          </button>
        </>
      )}
      <div className="pc-media-meta">
        {/* The known source label is never hidden for want of a date: dated form when we have the time,
            the label alone when we do not (a new Chat credit has no observation time), nothing without a label. */}
        {item.source && (
          <p className="pc-media-provenance" data-testid="media-provenance">
            {item.seenAt
              ? formatCopy(copy.provenance, { source: item.source, date: when(item.seenAt) })
              : item.source}
          </p>
        )}
        {view === 'ready' && large && sources[largeKey] === 'failed' && !thumbFailed && (
          <p className="pc-media-changed" data-testid="media-large-failed">
            {copy.refused}
          </p>
        )}
        {item.changedSince && (
          <p className="pc-media-changed" data-testid="media-changed">
            {copy.changedSince}
          </p>
        )}
      </div>
      <p className="pc-media-sr" role="status" aria-live="polite" data-testid="media-announce">
        {formatCopy(copy.announce, { n: index + 1, total: count, alt: item.alt })}
      </p>
    </div>
  );
}
